/**
 * feat-260 — the matched eyebrow pair for the two tenant language cards
 * (spec AT12, `design/copy.md` §1a/§1b/§2, `design/argued-lines.md` A2).
 *
 * ## Why the two strings live in ONE file, adjacent
 *
 * They are not two labels. They are a CONTRAST, and each is only correct while
 * the other exists. `Default language` and `Translate content into` are two
 * settings that were one setting until this feature, and the pair is what tells
 * a reader which is which before they have read either helper paragraph:
 *
 *     WHAT TRAINEES SEE   · Default language
 *     WHAT GETS TRANSLATED · Translate content into
 *
 * Two three-word phrases, same slot, same size, same weight, answering the same
 * question — *what does this control?* — about two different things. `copy.md`
 * §2: "one eyebrow with no partner is decoration; the pair is a contrast."
 *
 * So they sit one line apart, where a reviewer who rewords one sees the other.
 * `copy.md`: **"Keep them in lockstep if either is ever reworded."**
 *
 * ## Why a plain constants module and not the card that owns the design
 *
 * `TenantLanguageDefaultCard` is a file feat-260 does not own and is only
 * allowed to add one `<p>` to. Having it import from
 * `TenantTranslationLanguagesCard` would work, but it would make the sibling
 * depend on a React component module for a string — and any future test that
 * `jest.mock`s that component would silently hand it `undefined` and render an
 * empty eyebrow that no assertion in this repo would catch. A module with no
 * React in it cannot fail that way.
 *
 * ## The line most likely to be tidied away by someone who does not know
 *
 * `DEFAULT_LANGUAGE_EYEBROW`'s render site is a line this feature adds to a file
 * it does not own. `argued-lines.md` A2 calls it "the likeliest thing for a
 * future unrelated PR to tidy away, and the least likely to be questioned when
 * it goes" — nothing else in the codebase defends it, and a test that renders
 * only the NEW card cannot see it disappear. The test that holds it is
 * `TenantDashboard.languageEyebrows.test.tsx`, which renders BOTH cards through
 * the real dashboard and asserts both strings AND their document order.
 */

/** Above "Default language" — `TenantLanguageDefaultCard`. */
export const DEFAULT_LANGUAGE_EYEBROW = 'What trainees see';

/** Above "Translate content into" — `TenantTranslationLanguagesCard`. */
export const TRANSLATION_LANGUAGES_EYEBROW = 'What gets translated';

/** Shared class list, so the two eyebrows cannot drift apart visually. The
 *  parallelism is the mechanism; a difference in size or weight would break it
 *  as surely as a difference in wording. `text-inkMuted` (5.254:1 on white,
 *  measured by `design/contrast.js`) rather than the surrounding file's
 *  `text-gray-400` (2.539:1, below WCAG AA at 12px). */
export const EYEBROW_CLASSNAME =
  'text-xs font-semibold uppercase tracking-wide text-inkMuted mb-1';
